<template>
    <div class="Evaluation-modal-overlay">
        <transition name="modal-expand">
            <div v-if="isVisible" class="Evaluation-modal" @click.stop>
                <div class="modal-content">
                    <font-awesome-icon class="window-close-icon" :icon="['fas', 'window-close']" @click="close" />
                    <h2>Add {{ $labels.project }}</h2>
                    <div class="add">
                        <form @submit.prevent="addProject">
                            <div class="form-group">
                                <label for="role">{{ $labels.product }}:</label>
                                <select class="form-control rounded" id="product_id" name="product_id"
                                    v-model="newProject.product_id">
                                    <option value=""></option>
                                    <option v-for="product in products" :key="product.product_id"
                                        :value="product.product_id">{{
                                            product.name
                                        }}</option>
                                </select>
                            </div>
                            <div class="form-group">
                                <label for="name">{{ $labels.project }} Name:</label>
                                <input type="text" class="form-control rounded" id="name" name="name" placeholder="Enter Name"
                                    v-model="newProject.name" />
                            </div>
                            <div class="form-group">
                                <label for="start_date">Start Date:</label>
                                <input type="date" class="form-control rounded" id="start_date" name="start_date"
                                    placeholder="DD/MM/YYYY" v-model="newProject.start_date" />
                            </div>


                            <div class="form-group">
                                <label for="end_date">End Date:</label>
                                <input type="date" class="form-control rounded" id="end_date" name="end_date" :min="minDate"
                                    placeholder="DD/MM/YYYY" v-model="newProject.end_date" />
                            </div>

                            <div class="form-group">
                                <label for="location">Location:</label>
                                <input type="text" class="form-control rounded" id="location" name="location"
                                    placeholder="Enter Location" v-model="newProject.location" />
                            </div>

                            <div class="form-group buttons">
                                <button type="button" class="ins-assign-button" @click="viewSelectInstructors">Assign
                                    Instructors</button>
                                <button type="button" class="ins-assign-button" @click="viewSelectTrainees">Assign
                                    Trainees</button>
                            </div>
                            <div v-if="selectedInstructorNames.length > 0" class="row">
                                <fieldset class="logbook-details-box">
                                    <legend class="cell-header">Selected Instructors</legend>
                                    <div class="trainee-container">
                                        <div v-for="(name, index) in selectedInstructorNames" :key="index"
                                            class="trainee-box">
                                            {{ name }}
                                        </div>
                                    </div>
                                </fieldset>
                            </div>
                            <div v-if="selectedTraineeNames.length > 0" class="row">
                                <fieldset class="logbook-details-box">
                                    <legend class="cell-header">Selected Trainees</legend>
                                    <div class="trainee-container">
                                        <div v-for="(name, index) in selectedTraineeNames" :key="index"
                                            class="trainee-box">
                                            {{ name }}
                                        </div>
                                    </div>
                                </fieldset>
                            </div>
                            <div v-if="showAlert" class="alert alert-danger error-message" role="alert">
                                <div v-if="errorMessage" class="error-message">{{ errorMessage }}</div>
                            </div>
                            <div class="button-container">
                                <button class="ins-close-button" @click="close()">Close</button>
                                <button class="btn btn-primary rounded" type="submit" :disabled="processing"
                                    style="margin-right: 5%;">{{
                                        processing ? 'Processing...' : `Add ${$labels.project}` }}</button>
                            </div>
                        </form>
                    </div>
                </div>
                <select-trainee-modal v-if="showSelectTraineeModal" :propTrainees="trainees"
                    :initialSelectedTrainees="selectedTrainees" @trainees-selected="handleTraineesSelected"
                    @close="closeSelectTraineeModal"></select-trainee-modal>
                <select-instructor-modal v-if="showSelectInstructorModal" :propInstructors="instructors"
                    :initialSelectedInstructors="selectedInstructors" @instructors-selected="handleInstructorsSelected"
                    @close="closeSelectInstructorModal"></select-instructor-modal>
            </div>
        </transition>
    </div>
</template>

<script>
import axios from 'axios';
import SelectTraineeModal from './SelectTraineesModal.vue';
import SelectInstructorModal from './SelectInstructorsModal.vue';

export default {
    name: 'AddProjectModal',
    components: {
        SelectTraineeModal,
        SelectInstructorModal,
    },
    props: {
        logbookId: {
            type: Number,
            required: true,
        },
        mergedTasks: { // Accept the mergedTasks data from the parent component
            type: Array,
            required: true,
        },
    },
    data() {
        return {
            instructors: [],
            trainees: [],
            products: [],
            projects: [],
            newProject: {
                name: '',
                start_date: '',
                end_date: '',
                location: '',
                product_id: '',
                user_id: '',

            },
            showAlert: false,
            errorMessage: '',
            showSelectTraineeModal: false,
            selectedTrainees: [], // Store selected trainees here
            showSelectInstructorModal: false,
            selectedInstructors: [], // Store selected trainees here
            processing: false,
            isVisible: false,
        };
    },
    computed: {
        selectedTraineeNames() {
            return this.selectedTrainees
                .map(id => {
                    const trainee = this.trainees.find(t => t.user_id === id);
                    return trainee ? trainee.first_name + ' ' + trainee.last_name : 'Unknown';
                })
                .filter(name => name !== 'Unknown'); // Ensure only valid names are shown
        },
        selectedInstructorNames() {
            return this.selectedInstructors
                .map(id => {
                    const instructor = this.instructors.find(t => t.user_id === id);
                    return instructor ? instructor.first_name + ' ' + instructor.last_name : 'Unknown';
                })
                .filter(name => name !== 'Unknown'); // Ensure only valid names are shown
        },
        minDate() {
            return new Date().toISOString().split('T')[0];
        }
    },
    watch: {
        'newProject.product_id': function (newProductId) {
            if (newProductId && this.products) {
                const selectedProduct = this.products.find(product => product.product_id === newProductId);
                if (selectedProduct) {
                    this.newProject.name = selectedProduct.name;
                }
            }
        }
    },
    methods: {
        close() {
            this.$emit('close');
        },
        async addProject() {
            this.processing = true;
            try {
                // Check if end date is before start date
                if (this.newProject.end_date && this.newProject.start_date && this.newProject.end_date < this.newProject.start_date) {
                    this.errorMessage = 'End date cannot be before start date.';
                    this.showAlert = true;
                    return;
                }

                const token = localStorage.getItem('token');
                const headers = { Authorization: `Bearer ${token}` };

                // Create the project
                const projectResponse = await axios.post('/api/projects',
                    {
                        name: this.newProject.name,
                        start_date: this.newProject.start_date,
                        end_date: this.newProject.end_date,
                        location: this.newProject.location,
                        product_id: this.newProject.product_id,
                    },
                    { headers }
                );

                if (projectResponse.status === 201) {
                    const project = projectResponse.data;

                    // Assign trainees to the project
                    if (this.selectedTrainees.length > 0) {
                        console.log('selectedTraainees', this.selectedTrainees);
                        const data = {
                            user_ids: this.selectedTrainees,
                            role: 'trainee'
                        };
                        await axios.post(`/api/projects/${project.project_id}/add-users`, data, { headers });
                    }
                    if (this.selectedInstructors.length > 0) {
                        const instructorData = {
                            user_ids: this.selectedInstructors,
                            role: 'instructor'
                        };

                        // Step 1: Add instructors to project
                        await axios.post(`/api/projects/${project.project_id}/add-users`, instructorData, { headers });

                        try {
                            // Step 2: Fetch the project's logbooks
                            const logbookResponse = await axios.get(`/api/projects/${project.project_id}/logbooks`, { headers });
                            const logbooks = logbookResponse.data;

                            if (logbooks.length > 0) {
                                const logbookIds = logbooks.map(logbook => logbook.logbook_id);

                                // Step 3: Assign instructors to the logbooks
                                const logbookData = {
                                    user_ids: this.selectedInstructors,
                                    logbook_ids: logbookIds,
                                    role: 'instructor',
                                    project_id: project.project_id,
                                };

                                await axios.post(`/api/logbooks/add-users-to-logbook`, logbookData, { headers });

                                console.log('Instructors successfully assigned to logbooks.');
                            } else {
                                console.warn('No logbooks found for this project.');
                            }
                        } catch (logbookError) {
                            console.error('Error assigning instructors to logbooks:', logbookError);
                            if (logbookError.response?.data?.message === "The logbook ids field is required.") {
                                window.alert(`Instructors added successfully to the ${this.$labels.project}, but no logbooks were available.`);
                            } else {
                                throw logbookError;
                            }
                        }
                    }
                }

                window.alert(`${this.$labels.project} added successfully.`);

                this.newProject = {
                    name: '',
                    start_date: '',
                    end_date: '',
                    location: '',
                    product_id: '',
                };
                this.errorMessage = '';
                this.showAlert = false;
                this.processing = false;
                this.$emit('close');

            } catch (error) {
                console.error('Error adding Project:', error);

                if (error.isOffline === true) {
                    this.$emit('close');
                    return;
                }
                if (error.response && error.response.data && error.response.data.errors) {
                    const validationErrors = error.response.data.errors;
                    let errorMessage = 'Validation errors:';
                    for (const field in validationErrors) {
                        errorMessage += `\n${field}: ${validationErrors[field][0]}`;
                    }
                    this.errorMessage = errorMessage;
                } else {
                    this.errorMessage = `An error occurred while adding the ${this.$labels.project}.`;
                }

                this.showAlert = true;
            } finally {
                this.processing = false;
            }
        },
        async fetchProducts() {
            try {
                const token = localStorage.getItem('token');
                if (!token) {
                    console.display('unauthorized');
                    return;
                }

                const headers = {
                    Authorization: `Bearer ${token}`,
                };

                const response = await axios.get('/api/products', { headers });

                if (response.status === 200) {
                    this.products = response.data; // Populate the products array with API data
                }
            } catch (error) {
                console.error('Error retrieving products:', error);
                // Handle error
            }
        },
        async fetchUsers() {
            try {
                const token = localStorage.getItem('token');
                if (!token) {
                    console.display('unauthorized');
                    return;
                }

                const headers = {
                    Authorization: `Bearer ${token}`,
                };

                const response = await axios.get('/api/users', { headers });

                if (response.status === 200) {
                    // Filter the response data to include only users with the role 'instructor'
                    this.instructors = response.data.filter(user => Array.isArray(user.roles) && user.roles.includes('instructor'));
                    this.trainees = response.data.filter(user => Array.isArray(user.roles) && user.roles.includes('trainee'));
                    console.log('trainees before ', this.trainees);
                }
            } catch (error) {
                console.error('Error retrieving users:', error);
                // Handle error
            }
        },
        viewSelectTrainees() {
            this.showSelectTraineeModal = true; // Show the modal when the button is clicked
        },
        closeSelectTraineeModal() {
            this.showSelectTraineeModal = false; // Close the modal
        },
        handleTraineesSelected(trainees) {
            this.selectedTrainees = trainees; // Store the received trainee IDs
            console.log("Selected Trainees:", this.selectedTrainees); // Debugging
        },
        viewSelectInstructors() {
            this.showSelectInstructorModal = true; // Show the modal when the button is clicked
        },
        closeSelectInstructorModal() {
            this.showSelectInstructorModal = false; // Close the modal
        },
        handleInstructorsSelected(instructors) {
            this.selectedInstructors = instructors; // Store the received instructor IDs
            console.log("Selected Instructors:", this.selectedInstructors); // Debugging
        },

    },
    mounted() {
        this.isVisible = true;
        this.fetchUsers();
        this.fetchProducts();
    },
};
</script>

<style scoped>
.Evaluation-modal-overlay {
    position: fixed;
    top: 0px;
    left: 0;
    background-color: rgba(0, 0, 0, 0.5);
    display: flex;
    justify-content: center;
    width: 100%;
    height: 100vh;
    z-index: 1;
}

.Evaluation-modal {
    background-color: #ffffff;
    overflow-y: auto;
    /* Enables vertical scrolling if needed */
    margin-top: 25px;
    max-height: 95vh;
    width: min(90vw, 1000px);
    padding: 10px;
    border-radius: 8px;
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.2);
    position: fixed;
    display: flex;
    flex-direction: column;
    overflow-wrap: break-word;
    padding: 20px;
    z-index: 1002;
}

.modal-content {
    position: relative;
    max-height: 90vh;
    overflow-y: auto;
    padding: 15px;
    border: 1px solid #dddddd;
    border-radius: 3px;
}

.modal-content h2 {
    margin-top: 10px;
    font-size: 30px;
    margin-bottom: 30px;
}

.modal-title {
    text-align: center;
    margin-bottom: 20px;
}


@media (max-width: 768px) {

    /* Adjust the max-width as needed for iPad or other tablet sizes */
    .Evaluation-modal {
        padding: 5px;
        font-size: 0.9em;
        /* Reduce font size */
    }

    /* Further adjustments for other elements */
}



.alert {
    position: relative;
    padding: .75rem 1.25rem;
    margin-bottom: 0rem;
    border: 1px solid transparent;
    border-radius: .25rem;
}



button.btn:hover {
    background-color: #0c1a61;
    transition: transform 0.2s, box-shadow 0.2s;
}

.form-control.rounded {
    height: 40px;
}

.form-group {
    display: flex;
    align-items: center;
    margin-bottom: 20px;
}

.form-group label {
    width: 140px;
    margin-right: 40px;
    text-align: right;
    margin-bottom: 0px;
}

.form-group input.form-control,
.form-group select.form-control {
    flex: 1;
    margin-left: 0;
    margin-bottom: 0px;
    border: 1px solid #ccc
}

.form-control {
    border-radius: 5px;
}

#start_date,
#end_date {
    flex: 0 0 180px;
    /* don't grow, don't shrink, fixed 100px */
    width: 180px;
}

.form-group.buttons {
    justify-content: center;
    /* center horizontally */
    gap: 15px;
    /* space between buttons */
}

h1 {
    margin-bottom: 50px;
}

.button-container {
    display: flex;
    justify-content: space-between;
    /* Aligns items to the right */
    align-items: center;
    margin-top: 10px;
    /* Adjust as needed */
}

.ins-close-button {
    background-color: #6c757d;
    border: 1px solid #4d5358;
    color: white;
    width: 120px;
    padding: 5px 20px;
    border-radius: 5px;
    margin-left: 5%;
}

.ins-close-button:hover {
    background-color: #4d5358;
}

.logbook-details-box {
  border: 2px solid #858585;
  border-radius: 5px;
  margin: 3px auto;
  min-width: 95%;
  max-width: 95%;
  padding: 7px;
  margin-bottom: 20px;
}

.cell-header {
  font-weight: bold;
  font-family: Verdana, Geneva, Tahoma, sans-serif;
  font-size: 12px;
  background-color: #707070;
  padding: 2px 5px;
  color: white;
  border-radius: 2px;
  margin-bottom: 5px;
  margin-top: -20px;
  /* Set margin-right */
  text-align: left;
  display: inline-block;
  width: auto;
}

.trainee-container {
    display: flex;
    flex-wrap: wrap;
    /* Allows wrapping */
}

.trainee-box {
    display: inline-block;
    padding: 4px 12px;
    margin: 3px;
    border-radius: 8px;
    background-color: #f0f0f0;
    border: 1px solid #ccc;
    text-align: center;
    white-space: nowrap;
    font-size: 14px;
}

.ins-assign-button {
    margin-top: 0px;
    margin-bottom: 0px;
    margin-left: 15px;
    margin-right: 15px;
    border-radius: 5px;
    background-color: rgb(53, 131, 37);
    border: 1px solid rgb(26, 82, 16);
}

.ins-assign-button:hover {
    background-color: rgb(48, 112, 35);
    border: 1px solid rgb(23, 77, 13);
}

.ins-assign-button:disabled {
    background-color: #797979;
    border: 1px solid #3d3d3d;
    cursor: not-allowed;
    color: white;
    transition: none;
    transform: none;
}
</style>